import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  API,
  CullDTO,
  GeoCullDTO,
  PlayerCullDTO,
  TemplateDataSetEntryDTO,
  TemplateGroupDTO,
  TemplateVariableDTO,
  TemplateVariableType,
  TimeCullDTO,
} from '../../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { QueryParamService } from '../../../../services/lists/query-params';
import { PermissionService } from '../../../../services/permission.service';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { MsOptions } from '../../../../services/global/global-interfaces';
import { SbDateTime, SbLocalDateTime } from '../../../../services/api/api-date-time';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-template-dataset-entry-edit',
  templateUrl: './template-dataset-entry-edit.component.html',
  styleUrls: ['./template-dataset-entry-edit.component.scss'],
  standalone: false,
})
export class TemplateDatasetEntryEditComponent implements OnInit {
  TemplateVariableType = TemplateVariableType;

  templateGroupId: number;
  templateDatasetId: number;
  templateDatasetEntry: TemplateDataSetEntryDTO;
  templateDatasetEntryId: number;
  loading: boolean;
  form: UntypedFormGroup;
  regex: RegExp;
  templateVariables: TemplateVariableDTO[];

  from: SbDateTime;
  till: SbDateTime;

  msGeoOptions: MsOptions;
  msTimeOptions: MsOptions;
  msPlayerOptions: MsOptions;

  geoCulls: CullDTO[];
  timeCulls: CullDTO[];
  playerCulls: CullDTO[];

  selectedGeoCull: CullDTO;
  selectedTimeCull: CullDTO;
  selectedPlayerCull: CullDTO;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    public navService: NavigationService,
    public queryParamService: QueryParamService,
    public permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.templateDatasetId = null;
    this.templateDatasetEntryId = null;

    this.templateVariables = [];

    this.geoCulls = [];
    this.timeCulls = [];
    this.playerCulls = [];

    this.selectedGeoCull = null;
    this.selectedTimeCull = null;
    this.selectedPlayerCull = null;

    this.regex = new RegExp('http(s)?://([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');

    this.loading = true;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', []),
      Identifier: new UntypedFormControl('', [Validators.required]),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get Identifier() {
    return this.form.get('Identifier');
  }

  ngOnInit(): void {
    this.registerFormChangesListener();
    this.route.params.subscribe((params: Params) => {
      this.templateDatasetId = params.templateDatasetID;
      this.templateDatasetEntryId = params.templateDatasetEntryID;

      this.templateGroupId = this.queryParamService.getQueryParam('templateGroupID');
      this.api.mm_templating_provider_TemplateGroup_retrieveById(this.templateGroupId).subscribe({
        next: (templateGroup: TemplateGroupDTO) => {
          if (templateGroup) {
            this.templateVariables = templateGroup.variables;
            this.refreshFromServer();
          }
        },
      });
    });
    this.refreshFromServer();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      if (this.templateDatasetEntry) {
        this.templateDatasetEntry.name = value;
      }
    });

    this.Identifier.valueChanges.subscribe(value => {
      if (this.templateDatasetEntry) {
        this.templateDatasetEntry.identifier = value;
      }
    });
  }

  updateForm() {
    this.Name.setValue(this.templateDatasetEntry.name);
    this.Name.markAsTouched();

    this.Identifier.setValue(this.templateDatasetEntry.identifier);
    this.Identifier.markAsTouched();
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.templateDatasetEntryId) {
      this.templateDatasetEntry = new TemplateDataSetEntryDTO();
      this.templateDatasetEntry.values = {};
      for (const element of this.templateVariables) {
        this.templateDatasetEntry.values[element.name] = '';
      }
      this.loading = false;
    } else {
      this.loading = false;
      this.api
        .mm_templating_advertiser_TemplateDataSetEntry_retrieveAll(this.templateDatasetId, {
          ids: [this.templateDatasetEntryId],
        })
        .subscribe({
          next: (templateDatasetEntries: TemplateDataSetEntryDTO[]) => {
            this.templateDatasetEntry = templateDatasetEntries[0];

            this.selectedGeoCull = this.templateDatasetEntry.geoCull as CullDTO;
            this.selectedTimeCull = this.templateDatasetEntry.timeCull as CullDTO;
            this.selectedPlayerCull = this.templateDatasetEntry.playerCull as CullDTO;

            this.till = this.templateDatasetEntry.validTill;
            if (this.till) {
              this.from = this.templateDatasetEntry.createdAt;
            }
            this.updateForm();
            this.loading = false;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  update($event, type: string) {
    switch (type) {
      case 'GEO_CULLS':
        if ($event && $event.length > 0) {
          this.selectedGeoCull = $event[0];
        } else {
          this.selectedGeoCull = null;
        }
        break;
      case 'TIME_CULLS':
        if ($event && $event.length > 0) {
          this.selectedTimeCull = $event[0];
        } else {
          this.selectedTimeCull = null;
        }
        break;
      case 'PLAYER_CULLS':
        if ($event && $event.length > 0) {
          this.selectedPlayerCull = $event[0];
        } else {
          this.selectedPlayerCull = null;
        }
        break;
    }
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'GEO_CULLS':
        if (options) {
          this.msGeoOptions = options;
        }
        if (this.msGeoOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msPlayerOptions?.search),
                type: 'GeoCull',
              },
              this.msGeoOptions.pagination
            )
            .subscribe(culls => {
              this.geoCulls = culls;
            });
        }
        break;
      case 'TIME_CULLS':
        if (options) {
          this.msTimeOptions = options;
        }
        if (this.msTimeOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msPlayerOptions?.search),
                type: 'TimeCull',
              },
              this.msTimeOptions.pagination
            )
            .subscribe(culls => {
              this.timeCulls = culls;
            });
        }
        break;
      case 'PLAYER_CULLS':
        if (options) {
          this.msPlayerOptions = options;
        }
        if (this.msPlayerOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.util.encodeSearchQueryParam(false, this.msPlayerOptions?.search),
                type: 'PlayerCull',
              },
              this.msPlayerOptions.pagination
            )
            .subscribe(culls => {
              this.playerCulls = culls;
            });
        }
        break;
    }
  }

  createTemplateDatasetEntry() {
    if (this.form.valid) {
      this.loading = true;
      this.api
        .mm_templating_advertiser_TemplateDataSetEntry_createInDataSet(this.templateDatasetId, this.templateDatasetEntry)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (editedTemplateDatasetEntry: TemplateDataSetEntryDTO) => {
            this.templateDatasetEntry = editedTemplateDatasetEntry;
            this.navService.navigateToPoint(RoutingID.templateDatasetEntries, [{ id: 'templateDatasetID', value: this.templateDatasetId }]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateTemplateDatasetEntry() {
    if (this.form.valid) {
      this.loading = true;
      this.templateDatasetEntry.geoCull = this.selectedGeoCull as GeoCullDTO;
      this.templateDatasetEntry.timeCull = this.selectedTimeCull as TimeCullDTO;
      this.templateDatasetEntry.playerCull = this.selectedPlayerCull as PlayerCullDTO;

      this.api
        .mm_templating_advertiser_TemplateDataSetEntry_editInDataSet(this.templateDatasetId, this.templateDatasetEntry)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (editedTemplateDatasetEntry: TemplateDataSetEntryDTO) => {
            this.templateDatasetEntry = editedTemplateDatasetEntry;
            this.navService.navigateToPoint(RoutingID.templateDatasetEntries, [{ id: 'templateDatasetID', value: this.templateDatasetId }]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  validate(key: string, type: string): boolean {
    if (!key) {
      return false;
    } else if (this.templateDatasetEntry) {
      switch (type) {
        case this.TemplateVariableType.TEXT:
          return this.templateDatasetEntry.values[key]?.length > 0 && this.templateDatasetEntry.values[key]?.trim()?.length > 0;
        case this.TemplateVariableType.IMAGE:
          return (
            this.regex &&
            this.regex.test(this.templateDatasetEntry.values[key]) &&
            this.templateDatasetEntry.values[key]?.length > 0 &&
            this.templateDatasetEntry.values[key]?.trim()?.length > 0
          );
        case undefined:
        default:
          return false;
      }
    }
    return false;
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (!dates?.fromDate || !dates?.toDate) {
      return;
    }
    this.templateDatasetEntry.validFrom = new SbLocalDateTime(dates.fromDate.value);
    this.templateDatasetEntry.validTill = new SbLocalDateTime(dates.toDate.value);
  }
}
